import * as React from 'react';

import {
  Correctness,
  HELP_ARTICLE_LINKS,
  MediaResourceTypes,
  QuestionTypes,
} from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';

import { AlertMessage } from '~shared/components/AlertMessage';
import { SmallButton } from '~shared/components/buttons';
import {
  Dialog,
  DialogHeaderProps,
  DialogTitle,
} from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import ImagePreviewer from '~shared/components/ImageField/ImagePreviewer';
import { Inline } from '~shared/components/Inline';
import { MediaPlayer } from '~shared/components/MediaPlayer';
import { NumericInput } from '~shared/components/NumericInput';
import { RichTextRenderer } from '~shared/components/RichTextRenderer';
import { Spinner } from '~shared/components/Spinner/Spinner';
import { Stack } from '~shared/components/Stack';
import { toNumber, toPrecision } from '~shared/utils/number';
import {
  CreateQuestionMutateFunction,
  useCreateQuestionScore,
  useDeleteStudentScore,
  useUpsertStudentScore,
} from '~teacher/hook';
import { useDebouncedCallback } from '~teacher/hook/useDebouncedCallback';
import {
  QuestionScore,
  Student,
  StudentActivityResponse,
  StudentScore,
} from '~teacher/models/activities';
import { QuizQuestion } from '~teacher/models/quizzes';
import { proCheck } from '~teacher/util/proCheck';

import { assignedGradingWeight } from '../ActivityResult/scoreCalculation';
import { studentQuestionQuestionScore } from '../ActivityResult/scoreCalculation/calculateStudentQuestionScore/studentQuestionQuestionScore';
import { studentQuestionResponseScore } from '../ActivityResult/scoreCalculation/calculateStudentQuestionScore/studentQuestionResponseScore';
import { StudentScoreCorrectness } from './StudentScoreCorrectness';
import { StudentScoreCorrectnessLabel } from './StudentScoreCorrectnessLabel';
import {
  ActionType,
  useStudentScoreModalReducer,
} from './studentScoreModalReducer';
import { StudentScoreNavigation } from './StudentScoreNavigation';
import {
  calculateResponseCorrectness,
  calculateStudentScoreCorrectness,
  getCorrectnessLabel,
} from './utils';

const SCORE_PRECISION = 2;

/**
 * Return the response text based on the Question type.
 * Only handles Short Answer types for now, since that's all that
 * should be allowed in this modal.
 */
function getResponseText(response: StudentActivityResponse) {
  if (!response) return null;

  // FUTURE: handle different types of questions...
  if (response.type === QuestionTypes.FreeResponse) {
    return response.textResponse;
  } else {
    return null;
  }
}

export interface Props {
  activityInstanceId: number;
  canManuallyGrade: boolean;
  isRostered: boolean;
  selectStudentQuestion: ({
    studentId,
    questionId,
  }: {
    studentId: number;
    questionId: number;
  }) => void;
  studentIds: Array<number>;
  student: Student;
  question: QuizQuestion;
  handleModalCancel: () => void;
  studentScore: StudentScore;
  questionScore: QuestionScore;
  response: StudentActivityResponse;
  maybeOpenPaywall: () => void;
  onSave: (analyticsData) => void;
}

interface ContentProps extends Props, DialogHeaderProps {
  headingRef: React.Ref<HTMLElement>;
}

async function saveOrGetQuestionId({
  questionScore,
  createQuestionScore,
  gradingWeight,
}: {
  questionScore: QuestionScore;
  createQuestionScore: CreateQuestionMutateFunction;
  gradingWeight: number;
}) {
  if (questionScore) return questionScore.id;

  const createdQuestionScore = await createQuestionScore({
    gradingWeight: gradingWeight,
    ungradedScore: 0,
  });

  return createdQuestionScore.id;
}

function calculateHasChanges({
  formCorrectness,
  initialFormCorrectness,
  initialScore,
  formScore,
}: {
  formCorrectness: Correctness;
  initialFormCorrectness: Correctness;
  initialScore: number;
  formScore: number | string;
}) {
  return (
    formCorrectness !== initialFormCorrectness ||
    toPrecision(toNumber(String(initialScore)), SCORE_PRECISION) !==
      toPrecision(toNumber(String(formScore)), SCORE_PRECISION)
  );
}

export function StudentScoreModalContent({
  activityInstanceId,
  canManuallyGrade,
  isRostered,
  student,
  question,
  selectStudentQuestion,
  response,
  questionScore,
  studentScore,
  studentIds,
  handleModalCancel,
  maybeOpenPaywall,
  onSave,
  title,
  headingRef,
}: ContentProps) {
  const { isAutograded } = response;

  const initialFormCorrectness =
    calculateStudentScoreCorrectness(studentScore) ??
    calculateResponseCorrectness(response);

  const responseScore = studentQuestionResponseScore(
    response,
    question,
    questionScore
  );
  const initialScore =
    studentQuestionQuestionScore(studentScore) ?? responseScore;

  const gradingWeight = assignedGradingWeight({ question, questionScore });
  const responseCorrectness = calculateResponseCorrectness(response);
  const [
    { formCorrectness, isErrorMessageOpen, formScore, isModalDisabled },
    dispatch,
  ] = useStudentScoreModalReducer({
    gradingWeight: gradingWeight,
    responseCorrectness: responseCorrectness,
    ungradedScore: questionScore?.ungradedScore,
    initialCorrectness: initialFormCorrectness,
    initialScore: initialScore,
    responseScore: responseScore,
  });

  const hasChanges = calculateHasChanges({
    formCorrectness: formCorrectness,
    initialFormCorrectness: initialFormCorrectness,
    initialScore: initialScore,
    formScore: formScore,
  });

  const hasOverride = formCorrectness !== responseCorrectness;

  const showOverrideWarning = !isAutograded && hasOverride && !questionScore;
  const showRemoveOverrideWarning =
    !isAutograded &&
    !hasOverride &&
    questionScore &&
    Object.keys(questionScore.studentScores).length === 1 &&
    !!questionScore.studentScores[studentScore?.studentId];

  const {
    mutateAsync: createQuestionScore,
    status: createQuestionScoreStatus,
  } = useCreateQuestionScore({
    activityInstanceId,
    questionId: question.questionId as number,
  });

  const { mutate: upsertStudentScore, status: upsertStudentScoreStatus } =
    useUpsertStudentScore({
      activityInstanceId: activityInstanceId,
      questionId: question.questionId as number,
    });

  const { mutate: deleteStudentScore, status: deleteStudentScoreStatus } =
    useDeleteStudentScore({
      activityInstanceId: activityInstanceId,
      questionId: question.questionId as number,
    });

  const debouncedSetIsModalDisabled = useDebouncedCallback({
    callback: () => dispatch({ type: ActionType.EnableModal }),
    interval: 1000,
    dependencies: [],
  });

  const handleDeleteStudentScore = async () => {
    await deleteStudentScore({
      questionScoreId: questionScore.id,
      isRostered: isRostered,
      studentId: student.id,
    });
  };

  const handleSaveStudentScore = async () => {
    const questionScoreId = await saveOrGetQuestionId({
      questionScore: questionScore,
      createQuestionScore: createQuestionScore,
      gradingWeight: gradingWeight,
    });
    await upsertStudentScore({
      correctness: formCorrectness,
      questionScoreId: questionScoreId,
      isRostered: isRostered,
      score: typeof formScore === 'number' ? formScore : parseFloat(formScore),
      studentId: student.id,
    });
  };

  const handleSaveOrDelete = async () => {
    if (!hasChanges) {
      return; // do nothing, no changes.
    }

    if (
      formCorrectness === responseCorrectness &&
      formScore === responseScore
    ) {
      await handleDeleteStudentScore();
    } else {
      await handleSaveStudentScore();
    }

    onSave({
      question_weight: gradingWeight,
      original_correctness: calculateResponseCorrectness(response),
      modified_correctness: formCorrectness,
      original_score: response.isCorrect ? gradingWeight : 0,
      modified_score: formScore,
    });
  };

  const handleDoneOrRetry = async () => {
    dispatch({ type: ActionType.DisableModal });
    try {
      await handleSaveOrDelete();
      handleModalCancel();
    } catch (error) {
      console.error(error);
      dispatch({ type: ActionType.OpenError });
      debouncedSetIsModalDisabled();
    }
  };

  const onChangeStudent = async (newStudentId: number): Promise<void> => {
    dispatch({ type: ActionType.CloseError });
    try {
      await handleSaveOrDelete();
      selectStudentQuestion({
        studentId: newStudentId,
        questionId: question.questionId as number,
      });
    } catch (error) {
      console.error(error);
      dispatch({ type: ActionType.OpenError });
      debouncedSetIsModalDisabled();
    }
  };

  const handleCorrectnessChange = React.useCallback(
    (correctness: Correctness) => {
      maybeOpenPaywall();
      if (!canManuallyGrade) return;

      dispatch({ type: ActionType.UpdateCorrectness, payload: correctness });
    },
    [maybeOpenPaywall, canManuallyGrade, dispatch]
  );

  const handleScoreValueChange = React.useCallback(
    (score: number) => {
      maybeOpenPaywall();
      if (!canManuallyGrade) return false;

      dispatch({
        type: ActionType.UpdateScore,
        payload: score,
      });
    },
    [maybeOpenPaywall, canManuallyGrade, dispatch]
  );

  const clearStudentScoreOverride = () => {
    dispatch({ type: ActionType.ClearStudentScore });
  };

  const handleDiscardChanges = () => {
    dispatch({ type: ActionType.CloseError });
    dispatch({
      type: ActionType.UpdateCorrectness,
      payload: initialFormCorrectness,
    });
  };

  const pitchMessage = proCheck.canShowAdvancedQuizEditor()
    ? translate(
        'Manually grade responses and give partial marks with Socrative Pro!'
      )
    : translate(
        'Customize question weight, manually grade responses, and give partial marks with Socrative Pro!'
      );

  const denominatorString =
    question.gradingWeight === 1
      ? `/ ${translate('1 point')}`
      : `/ ${translate('{0} points').format(question.gradingWeight)}`;

  const isControlsDisabled =
    createQuestionScoreStatus === 'loading' ||
    upsertStudentScoreStatus === 'loading' ||
    deleteStudentScoreStatus === 'loading' ||
    isModalDisabled;

  const errorMessageStyles = cx('pt-4', {
    'opacity-66': isControlsDisabled,
  });

  return (
    <div className="flex flex-col max-h-full min-h-0 text-blue-900">
      <div className="flex items-start justify-between flex-none">
        <h2 className="flex m-0 py-2">
          <DialogTitle weight="bold" ref={headingRef}>
            {title}
          </DialogTitle>
        </h2>
        <div className="flex-none">
          <Inline space="4">
            {isControlsDisabled ? (
              <div className="relative h-12">
                <div
                  className="absolute right-0 flex items-center h-12 pl-5"
                  style={{
                    background:
                      'linear-gradient(to right, rgba(255,255,255,0) 0%, #fff 25%)',
                  }}
                >
                  <Spinner size="small" />
                </div>
              </div>
            ) : null}
            <StudentScoreNavigation
              currentStudentId={student.id}
              isDisabled={isControlsDisabled}
              changeStudent={onChangeStudent}
              studentIds={studentIds}
            />
            <SmallButton
              isDisabled={isControlsDisabled}
              onClick={handleDoneOrRetry}
            >
              {translate('Done')}
            </SmallButton>
          </Inline>
        </div>
      </div>
      <div className="pt-4">
        <Divider />
      </div>
      {isErrorMessageOpen && (
        <div className={errorMessageStyles}>
          <AlertMessage tone="critical" showIcon>
            {translate(
              'An error prevented your change from being saved. Please try again.'
            )}
            <div className="flex pt-4">
              <Inline space="4">
                <SmallButton
                  isDisabled={isControlsDisabled}
                  isOnWhite={false}
                  onClick={handleDoneOrRetry}
                >
                  {translate('Retry')}
                </SmallButton>
                <SmallButton
                  isDisabled={isControlsDisabled}
                  appearance={'error'}
                  isOnWhite={false}
                  onClick={handleDiscardChanges}
                >
                  {translate('Discard Changes')}
                </SmallButton>
              </Inline>
            </div>
          </AlertMessage>
        </div>
      )}
      <div className="relative flex-1 min-h-0 mt-8 overflow-y-auto">
        <Stack space="4">
          <Stack space="2">
            {question.questionText ? (
              <RichTextRenderer
                className="flex-grow-1 h-auto font-display text-display font-500 text-blue-900 leading-8 max-h-full min-h-full overflow-auto"
                htmlText={question.questionText}
              />
            ) : null}
            {question.questionImage ? (
              <ImagePreviewer
                imageURL={question.questionImage.urlS}
                imageStyle="max-h-70 rounded-2"
                showOutline
                containerStyle="max-w-70 max-h-70 mt-0 rounded-2"
              />
            ) : null}
            {question.questionVideo ? (
              <MediaPlayer
                key={`question-video-${question.questionId}`}
                url={question.questionVideo?.url}
                type={MediaResourceTypes.Video}
                controls
                containerStyle="w-70 h-70 mt-0 rounded-2 ml-1 mt-2"
              />
            ) : null}
          </Stack>
          <span className="font-sans text-base leading-8 text-blue-900">
            {getResponseText(response)}
          </span>
        </Stack>
      </div>
      <div className="pt-8">
        <Divider />
      </div>
      <div className="pt-8">
        <div className="flex items-start justify-between">
          <Inline space="4" alignY="center">
            <StudentScoreCorrectness
              correctness={formCorrectness}
              isControlsDisabled={isControlsDisabled}
              onCorrectnessChange={handleCorrectnessChange}
            />
            <StudentScoreCorrectnessLabel
              isAutograded={isAutograded}
              hasOverride={hasOverride}
              isControlsDisabled={isControlsDisabled}
              onClick={clearStudentScoreOverride}
            >
              {getCorrectnessLabel({
                responseCorrectness: responseCorrectness,
                formCorrectness: formCorrectness,
              })}
            </StudentScoreCorrectnessLabel>
          </Inline>
          <div className="flex items-center">
            <div className="w-28">
              <NumericInput
                min={0}
                max={99.99}
                step={0.5}
                precision={2}
                value={formScore?.toString() ?? ' '}
                textAlign="right"
                onChange={handleScoreValueChange}
                disabled={isControlsDisabled}
              />
            </div>
            <div className="ml-2">
              <span className="font-sans text-base leading-8 text-blue-900 whitespace-no-wrap">
                {denominatorString}
              </span>
            </div>
          </div>
        </div>
        {showOverrideWarning || showRemoveOverrideWarning ? (
          <div className="pt-8">
            <AlertMessage tone="caution" showIcon>
              {showOverrideWarning
                ? translate(
                    'This question will now count toward the total score for all students. Unmarked responses will be counted as incorrect.'
                  )
                : translate(
                    'This question will no longer count toward the total score for all students.'
                  )}
            </AlertMessage>
          </div>
        ) : null}
        {!canManuallyGrade ? (
          <div className="pt-8">
            <AlertMessage tone="promote" showIcon>
              {pitchMessage}{' '}
              <a
                href={HELP_ARTICLE_LINKS.MANUAL_GRADING}
                rel="noopener noreferrer"
                target="_blank"
              >
                {translate('Learn More')}
              </a>
            </AlertMessage>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function StudentScoreModal(props: Props) {
  return (
    <Dialog
      id="student-score"
      size="medium"
      position="top"
      open
      title={props.student?.name}
      onClose={props.handleModalCancel}
      hasCustomCloseButton
    >
      {(headerProps) => (
        <StudentScoreModalContent
          key={`${props.student?.id}-${props.question?.questionId}`}
          {...headerProps}
          {...props}
        />
      )}
    </Dialog>
  );
}
